import { useEffect, useState, useCallback } from 'react'
import {
  ShieldCheck, AlertTriangle, Clock, RefreshCw, Radio,
  Phone, MessageSquare, Battery, MapPin, Activity,
  Users, CheckCircle, Zap, X, ChevronRight, Bell, Map, LayoutGrid,
} from 'lucide-react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import api from '../../services/api'

function MapFitBounds({ sessions }: { sessions: any[] }) {
  const map = useMap()
  useEffect(() => {
    const pts = sessions.filter(s => s.last_latitude && s.last_longitude)
    if (!pts.length) return
    // Let the flex container finish painting before measuring — Leaflet needs
    // correct dimensions for fitBounds to produce the right zoom/center
    const t = setTimeout(() => {
      map.invalidateSize()
      if (pts.length === 1) {
        map.setView([pts[0].last_latitude, pts[0].last_longitude], 13)
      } else {
        const lats = pts.map((s: any) => s.last_latitude)
        const lngs = pts.map((s: any) => s.last_longitude)
        map.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [40, 40] })
      }
    }, 100)
    return () => clearTimeout(t)
  }, [sessions.length])
  return null
}

const ACTIVITY_LABELS: Record<string, string> = {
  field_inspection: 'Field Inspection',
  remote_site: 'Remote Site Work',
  after_hours: 'After Hours Work',
  confined_space_prep: 'Confined Space Prep',
  driving: 'Lone Driving',
  data_collection: 'Data Collection',
  maintenance: 'Remote Maintenance',
  other: 'Other',
}

function minutesAgo(ts: string | null): string {
  if (!ts) return '—'
  const diff = Math.round((Date.now() - new Date(ts).getTime()) / 60000)
  if (diff < 1) return 'just now'
  if (diff < 60) return `${diff}m ago`
  return `${Math.floor(diff / 60)}h ${diff % 60}m ago`
}

function timeStr(ts: string | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
}

function initials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
}

function workerStatus(sess: any): { label: string; color: string; bg: string } {
  if (sess.status === 'sos') return { label: 'SOS', color: '#D93025', bg: '#FFEBE6' }
  if (sess.overdue) return { label: 'Overdue', color: '#F5921E', bg: '#FEF3E2' }
  return { label: 'Safe', color: '#3DB54A', bg: '#E8F8EA' }
}

function avatarColor(name: string): string {
  const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#0EA5E9', '#EF4444']
  const i = name.charCodeAt(0) % colors.length
  return colors[i]
}

export default function LoneWorkerMonitor() {
  const [data, setData] = useState<any>({ data: [], total: 0, stats: null, recent_alerts: [] })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const [filter, setFilter] = useState<'all' | 'safe' | 'overdue' | 'sos'>('all')
  const [countdown, setCountdown] = useState(15)
  const [resolvingAlert, setResolvingAlert] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')
  const [smsCompose, setSmsCompose] = useState(false)
  const [smsText, setSmsText] = useState('')
  const [smsSending, setSmsSending] = useState(false)
  const [smsResult, setSmsResult] = useState<{ ok: boolean; msg: string } | null>(null)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true)
    try {
      const { data: res } = await api.get('/lone-worker/monitor')
      setData(res)
      // Keep selected worker in sync
      if (selected) {
        const updated = res.data?.find((s: any) => s.id === selected.id)
        if (updated) setSelected(updated)
      }
    } catch {}
    setLoading(false)
    setRefreshing(false)
    setCountdown(15)
  }, [selected?.id])

  useEffect(() => {
    load()
    const interval = setInterval(() => load(true), 15000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const tick = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 15), 1000)
    return () => clearInterval(tick)
  }, [])

  const resolveAlert = async (alertId: string) => {
    setResolvingAlert(alertId)
    await api.post(`/lone-worker/alerts/${alertId}/resolve`).catch(() => {})
    await load(true)
    setResolvingAlert(null)
  }

  const sendSms = async () => {
    if (!selected || !smsText.trim()) return
    setSmsSending(true)
    setSmsResult(null)
    try {
      await api.post(`/lone-worker/sessions/${selected.id}/sms`, { message: smsText.trim() })
      setSmsResult({ ok: true, msg: `SMS sent to ${selected.worker_name}` })
      setSmsText('')
      setTimeout(() => { setSmsCompose(false); setSmsResult(null) }, 2500)
    } catch (err: any) {
      setSmsResult({ ok: false, msg: err.response?.data?.error || 'SMS failed' })
    }
    setSmsSending(false)
  }

  const openSms = (sess: any) => {
    setSelected(sess)
    setSmsText(`Hi ${sess.worker_name?.split(' ')[0] || 'there'}, checking in — please confirm you're safe.`)
    setSmsCompose(true)
    setSmsResult(null)
  }

  const selectWorker = (sess: any) => {
    setSelected((prev: any) => prev?.id === sess.id ? null : sess)
    setSmsCompose(false)
    setSmsResult(null)
  }

  const sessions: any[] = data.data || []
  const stats = data.stats || { active: 0, safe: 0, overdue: 0, sos: 0, compliance: 100 }
  const recentAlerts: any[] = data.recent_alerts || []

  const filtered = sessions.filter(s => {
    if (filter === 'safe') return !s.overdue && s.status === 'active'
    if (filter === 'overdue') return s.overdue
    if (filter === 'sos') return s.status === 'sos'
    return true
  })

  if (loading) return (
    <div className="flex items-center justify-center h-64" style={{ color: 'var(--text-muted)' }}>
      <RefreshCw size={20} className="animate-spin mr-2" /> Loading monitor…
    </div>
  )

  return (
    <div className="space-y-4" style={{ minHeight: '80vh' }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Lone Worker Monitoring</h1>
            <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: '#E8F8EA', color: '#3DB54A' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" /> Live
            </span>
          </div>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Real-time visibility of your lone workers and high-risk activities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <button
              onClick={() => setViewMode('list')}
              className="flex items-center gap-1.5 text-sm px-3 py-2"
              style={{ background: viewMode === 'list' ? 'var(--accent)' : 'var(--bg)', color: viewMode === 'list' ? 'white' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
            >
              <LayoutGrid size={14} /> List
            </button>
            <button
              onClick={() => setViewMode('map')}
              className="flex items-center gap-1.5 text-sm px-3 py-2"
              style={{ background: viewMode === 'map' ? 'var(--accent)' : 'var(--bg)', color: viewMode === 'map' ? 'white' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
            >
              <Map size={14} /> Map
            </button>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
            style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : `Refresh in ${countdown}s`}
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {[
          { label: 'Active Workers', value: stats.active, icon: <Users size={22} />, color: '#3B82F6', bg: '#EFF6FF', sub: `${stats.safe} safe` },
          { label: 'Overdue Check-ins', value: stats.overdue, icon: <Clock size={22} />, color: stats.overdue > 0 ? '#F59E0B' : '#94A3B8', bg: stats.overdue > 0 ? '#FFFBEB' : '#F8FAFC', sub: stats.overdue > 0 ? 'Needs attention' : 'All on time' },
          { label: 'SOS Active', value: stats.sos, icon: <Zap size={22} />, color: stats.sos > 0 ? '#D93025' : '#94A3B8', bg: stats.sos > 0 ? '#FFEBE6' : '#F8FAFC', sub: stats.sos > 0 ? 'Emergency!' : 'None active' },
          { label: 'Completed Today', value: stats.completed_today ?? 0, icon: <CheckCircle size={22} />, color: '#10B981', bg: '#ECFDF5', sub: `of ${stats.sessions_today ?? 0} today` },
          { label: 'Compliance', value: `${stats.compliance}%`, icon: <ShieldCheck size={22} />, color: stats.compliance >= 95 ? '#3DB54A' : stats.compliance >= 80 ? '#F59E0B' : '#D93025', bg: stats.compliance >= 95 ? '#E8F8EA' : stats.compliance >= 80 ? '#FFFBEB' : '#FFEBE6', sub: '30-day average' },
        ].map(tile => (
          <div key={tile.label} className="card flex items-start gap-3 p-4">
            <div className="flex-shrink-0 p-2 rounded-xl" style={{ background: tile.bg, color: tile.color }}>
              {tile.icon}
            </div>
            <div>
              <div className="text-2xl font-extrabold leading-tight" style={{ color: tile.color }}>{tile.value}</div>
              <div className="text-xs font-semibold mt-0.5" style={{ color: 'var(--text)' }}>{tile.label}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{tile.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* SOS Banner */}
      {stats.sos > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm" style={{ background: '#FFEBE6', color: '#D93025', border: '2px solid #D93025' }}>
          <AlertTriangle size={18} fill="#D93025" className="flex-shrink-0 animate-pulse" />
          <span>EMERGENCY: {stats.sos} active SOS alert{stats.sos > 1 ? 's' : ''} — immediate attention required</span>
          <button
            onClick={() => setFilter('sos')}
            className="ml-auto text-xs px-3 py-1 rounded-lg font-bold"
            style={{ background: '#D93025', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            View SOS →
          </button>
        </div>
      )}

      {/* Main monitoring layout */}
      <div className="flex gap-4" style={{ minHeight: '480px' }}>

        {/* Left: Worker list */}
        <div className="card p-0 overflow-hidden flex flex-col" style={{ width: 280, flexShrink: 0 }}>
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
            <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
              Active Workers <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>{sessions.length}</span>
            </span>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 px-3 py-2 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
            {(['all', 'safe', 'overdue', 'sos'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="text-xs px-2.5 py-1 rounded-lg font-semibold capitalize transition-all"
                style={{
                  background: filter === f ? 'var(--accent)' : 'transparent',
                  color: filter === f ? 'white' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center px-4">
                <ShieldCheck size={28} style={{ color: 'var(--text-muted)' }} className="mb-2" />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {sessions.length === 0 ? 'No active sessions' : 'No workers in this category'}
                </p>
              </div>
            ) : (
              filtered.map(sess => {
                const st = workerStatus(sess)
                const isSelected = selected?.id === sess.id
                return (
                  <button
                    key={sess.id}
                    onClick={() => setSelected(isSelected ? null : sess)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', textAlign: 'left', cursor: 'pointer',
                      background: isSelected ? 'var(--accent-light, #EFF6FF)' : 'transparent',
                      borderBottom: '1px solid var(--border)', border: 'none',
                      borderLeft: isSelected ? `3px solid var(--accent)` : '3px solid transparent',
                    }}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: avatarColor(sess.worker_name || '?') }}>
                      {initials(sess.worker_name || '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{sess.worker_name}</span>
                        <span className="text-xs font-bold ml-1 flex-shrink-0" style={{ color: st.color }}>{st.label}</span>
                      </div>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {ACTIVITY_LABELS[sess.activity_type] || sess.activity_type}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {sess.overdue ? (
                          <span style={{ color: '#F59E0B' }}>{sess.minutes_overdue}m overdue</span>
                        ) : (
                          minutesAgo(sess.last_checkin_at)
                        )}
                        {sess.location_description && ` · ${sess.location_description}`}
                      </p>
                    </div>
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Center: Session board or Map */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">

          {sessions.length === 0 ? (
            <div className="card flex-1 flex flex-col items-center justify-center text-center py-16">
              <ShieldCheck size={48} className="mb-4" style={{ color: 'var(--text-muted)' }} />
              <p className="text-lg font-semibold" style={{ color: 'var(--text)' }}>No active lone worker sessions</p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Workers using the app will appear here when sessions are started</p>
            </div>
          ) : viewMode === 'map' ? (
            <div className="card overflow-hidden flex-1" style={{ minHeight: 480, padding: 0 }}>
              <MapContainer
                center={[-33.8688, 151.2093]}
                zoom={10}
                style={{ height: '100%', minHeight: 480, width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <MapFitBounds sessions={sessions} />
                {sessions.map(sess => {
                  if (!sess.last_latitude || !sess.last_longitude) return null
                  const st = workerStatus(sess)
                  const isSelected = selected?.id === sess.id
                  return (
                    <CircleMarker
                      key={sess.id}
                      center={[sess.last_latitude, sess.last_longitude]}
                      radius={isSelected ? 16 : 12}
                      pathOptions={{
                        color: 'white',
                        weight: 2,
                        fillColor: st.color,
                        fillOpacity: 0.95,
                      }}
                      eventHandlers={{ click: () => selectWorker(sess) }}
                    >
                      <Popup>
                        <div style={{ minWidth: 180 }}>
                          <div className="font-bold" style={{ fontSize: 13, color: '#1e293b' }}>{sess.worker_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{ACTIVITY_LABELS[sess.activity_type] || sess.activity_type}</div>
                          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color }}>● {st.label}</span>
                            {sess.battery_level != null && <span style={{ fontSize: 11, color: '#64748b' }}>🔋 {sess.battery_level}%</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Last check-in: {minutesAgo(sess.last_checkin_at)}</div>
                          {sess.location_description && <div style={{ fontSize: 11, color: '#64748b' }}>{sess.location_description}</div>}
                        </div>
                      </Popup>
                    </CircleMarker>
                  )
                })}
              </MapContainer>
              {sessions.some(s => !s.last_latitude) && (
                <div className="px-4 py-2 text-xs flex items-center gap-1.5" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                  <MapPin size={11} />
                  {sessions.filter(s => !s.last_latitude).length} worker{sessions.filter(s => !s.last_latitude).length > 1 ? 's' : ''} without GPS — shown in list view only
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {sessions.map(sess => {
                const st = workerStatus(sess)
                return (
                  <div
                    key={sess.id}
                    className="card p-4 cursor-pointer transition-all"
                    style={{
                      borderLeft: `4px solid ${st.color}`,
                      background: sess.status === 'sos' ? '#FFF5F5' : sess.overdue ? '#FFFBF0' : 'var(--surface)',
                    }}
                    onClick={() => selectWorker(sess)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: avatarColor(sess.worker_name || '?') }}>
                          {initials(sess.worker_name || '?')}
                        </div>
                        <div>
                          <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{sess.worker_name}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{ACTIVITY_LABELS[sess.activity_type] || sess.activity_type}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: st.bg, color: st.color }}>
                        {st.label}
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {sess.site_name && (
                        <div className="flex items-center gap-1.5">
                          <MapPin size={11} /> {sess.site_name}
                          {sess.location_description && ` — ${sess.location_description}`}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Clock size={11} />
                          Last check-in: <span className={sess.overdue ? 'font-bold' : ''} style={{ color: sess.overdue ? '#F59E0B' : 'inherit' }}>
                            {minutesAgo(sess.last_checkin_at)}
                          </span>
                        </span>
                        {sess.battery_level != null && (
                          <span className="flex items-center gap-1">
                            <Battery size={11} style={{ color: sess.battery_level < 20 ? '#D93025' : '#94A3B8' }} />
                            <span style={{ color: sess.battery_level < 20 ? '#D93025' : 'inherit' }}>{sess.battery_level}%</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Activity size={11} />
                        Next due: <span className="font-medium">{timeStr(sess.next_checkin_due)}</span>
                        <span className="ml-auto">Every {sess.checkin_interval_minutes}m</span>
                      </div>
                    </div>

                    {sess.active_alerts?.length > 0 && (
                      <div className="mt-3 pt-2 border-t flex items-center gap-1.5 text-xs font-semibold" style={{ borderColor: '#FECACA', color: '#D93025' }}>
                        <AlertTriangle size={12} fill="#D93025" />
                        {sess.active_alerts.length} active alert{sess.active_alerts.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right: Worker detail panel */}
        {selected && (
          <div className="card p-0 overflow-hidden flex flex-col" style={{ width: 300, flexShrink: 0 }}>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
              <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Worker Detail</span>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Identity */}
              <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-white" style={{ background: avatarColor(selected.worker_name || '?') }}>
                    {initials(selected.worker_name || '?')}
                  </div>
                  <div>
                    <p className="font-bold" style={{ color: 'var(--text)' }}>{selected.worker_name}</p>
                    {(() => { const st = workerStatus(selected); return (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }}>● {st.label}</span>
                    )})()}
                  </div>
                </div>
              </div>

              {/* Session details */}
              <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Session Details</p>
                {[
                  { label: 'Activity', value: ACTIVITY_LABELS[selected.activity_type] || selected.activity_type },
                  { label: 'Site', value: selected.site_name || '—' },
                  { label: 'Location', value: selected.location_description || '—' },
                  { label: 'Started', value: timeStr(selected.created_at) },
                  { label: 'Expected End', value: timeStr(selected.expected_end) },
                  { label: 'Check-in Every', value: `${selected.checkin_interval_minutes} min` },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                    <span className="font-medium text-right max-w-[160px]" style={{ color: 'var(--text)' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Status */}
              <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Status</p>
                {[
                  {
                    label: 'Last Check-in', icon: <Clock size={12} />,
                    value: minutesAgo(selected.last_checkin_at),
                    valueColor: selected.overdue ? '#F59E0B' : '#3DB54A',
                  },
                  {
                    label: 'Next Due', icon: <Activity size={12} />,
                    value: timeStr(selected.next_checkin_due),
                    valueColor: selected.overdue ? '#D93025' : 'var(--text)',
                  },
                  ...(selected.battery_level != null ? [{
                    label: 'Battery', icon: <Battery size={12} />,
                    value: `${selected.battery_level}%`,
                    valueColor: selected.battery_level < 20 ? '#D93025' : selected.battery_level < 40 ? '#F59E0B' : '#3DB54A',
                  }] : []),
                  ...(selected.last_latitude ? [{
                    label: 'GPS', icon: <MapPin size={12} />,
                    value: `${selected.last_latitude.toFixed(4)}, ${selected.last_longitude.toFixed(4)}`,
                    valueColor: 'var(--text)',
                  }] : []),
                ].map(({ label, icon, value, valueColor }) => (
                  <div key={label} className="flex items-center justify-between text-xs py-1.5">
                    <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>{icon} {label}</span>
                    <span className="font-semibold" style={{ color: valueColor }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Active alerts */}
              {selected.active_alerts?.length > 0 && (
                <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#D93025' }}>Active Alerts</p>
                  <div className="space-y-2">
                    {selected.active_alerts.map((alert: any) => (
                      <div key={alert.id} className="p-2.5 rounded-lg" style={{ background: '#FFEBE6' }}>
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-xs font-medium" style={{ color: '#D93025' }}>{alert.message || alert.alert_type?.replace(/_/g, ' ')}</p>
                          <button
                            onClick={() => resolveAlert(alert.id)}
                            disabled={resolvingAlert === alert.id}
                            className="text-xs font-bold px-2 py-0.5 rounded flex-shrink-0"
                            style={{ background: '#D93025', color: 'white', border: 'none', cursor: 'pointer', opacity: resolvingAlert === alert.id ? 0.6 : 1 }}
                          >
                            {resolvingAlert === alert.id ? '…' : 'Resolve'}
                          </button>
                        </div>
                        <p className="text-xs mt-1" style={{ color: '#D93025', opacity: 0.7 }}>{minutesAgo(alert.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent check-ins */}
              {selected.recent_checkins?.length > 0 && (
                <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Check-in History</p>
                  <div className="space-y-1">
                    {selected.recent_checkins.map((ci: any) => (
                      <div key={ci.id} className="flex items-center justify-between text-xs py-1">
                        <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                          <Clock size={10} /> {timeStr(ci.created_at)}
                        </span>
                        <span className="font-medium" style={{ color: '#3DB54A' }}>Safe</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
              {selected.worker_mobile ? (
                <div className="mb-3 px-3 py-2 rounded-lg flex items-center justify-between" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Mobile</span>
                  <a href={`tel:${selected.worker_mobile}`} className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                    <Phone size={12} /> {selected.worker_mobile}
                  </a>
                </div>
              ) : (
                <div className="mb-3 px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No mobile number on file</span>
                </div>
              )}

              {!smsCompose ? (
                <button
                  onClick={() => openSms(selected)}
                  disabled={!selected.worker_mobile}
                  className="btn btn-primary text-xs w-full flex items-center justify-center gap-1.5"
                  style={{ width: '100%' }}
                >
                  <MessageSquare size={13} /> Send SMS via Twilio
                </button>
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={smsText}
                    onChange={e => setSmsText(e.target.value)}
                    maxLength={320}
                    rows={3}
                    placeholder="Type your message…"
                    className="w-full text-xs rounded-lg px-3 py-2 resize-none"
                    style={{ border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' }}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{smsText.length}/320</span>
                    <div className="flex gap-2">
                      <button onClick={() => { setSmsCompose(false); setSmsResult(null) }} className="btn btn-secondary text-xs px-3">Cancel</button>
                      <button onClick={sendSms} disabled={smsSending || !smsText.trim()} className="btn btn-primary text-xs px-3">
                        {smsSending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                  {smsResult && (
                    <div className="text-xs px-3 py-2 rounded-lg font-medium" style={{ background: smsResult.ok ? '#E8F8EA' : '#FFEBE6', color: smsResult.ok ? '#3DB54A' : '#D93025' }}>
                      {smsResult.ok ? '✓ ' : '✗ '}{smsResult.msg}
                    </div>
                  )}
                </div>
              )}
              {selected.status === 'sos' && (
                <button
                  className="col-span-2 text-xs flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold"
                  style={{ background: '#D93025', color: 'white', border: 'none', cursor: 'pointer' }}
                  onClick={() => resolveAlert(selected.active_alerts?.[0]?.id)}
                >
                  <Zap size={13} /> Escalate / Resolve SOS
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-3 gap-4">

        {/* Performance */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Performance</p>
            <span className="text-xs px-2 py-0.5 rounded-lg" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>Today</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Active Now', value: stats.active, color: '#3B82F6' },
              { label: 'Overdue', value: stats.overdue, color: stats.overdue > 0 ? '#F59E0B' : '#94A3B8' },
              { label: 'SOS Today', value: stats.sos, color: stats.sos > 0 ? '#D93025' : '#94A3B8' },
              { label: 'Compliance', value: `${stats.compliance}%`, color: stats.compliance >= 95 ? '#3DB54A' : '#F59E0B' },
            ].map(item => (
              <div key={item.label} className="text-center p-3 rounded-xl" style={{ background: 'var(--bg)' }}>
                <div className="text-xl font-extrabold" style={{ color: item.color }}>{item.value}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Activity breakdown */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Activity Types</p>
          </div>
          {sessions.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm" style={{ color: 'var(--text-muted)' }}>No active sessions</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(
                sessions.reduce((acc: any, s) => {
                  const label = ACTIVITY_LABELS[s.activity_type] || s.activity_type
                  acc[label] = (acc[label] || 0) + 1
                  return acc
                }, {})
              ).map(([label, count]: any) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: 'var(--text)' }}>{label}</span>
                      <span className="font-bold" style={{ color: 'var(--text-secondary)' }}>{count}</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: 'var(--border)' }}>
                      <div className="h-1.5 rounded-full" style={{ background: 'var(--accent)', width: `${(count / sessions.length) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Latest alerts */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Latest Alerts</p>
            <Bell size={14} style={{ color: 'var(--text-muted)' }} />
          </div>
          {recentAlerts.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm" style={{ color: 'var(--text-muted)' }}>No recent alerts</div>
          ) : (
            <div className="space-y-2">
              {recentAlerts.slice(0, 5).map((alert: any) => (
                <div key={alert.id} className="flex items-start gap-2 text-xs">
                  <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5" style={{
                    background: alert.severity === 'critical' ? '#D93025' : alert.severity === 'high' ? '#F59E0B' : '#94A3B8'
                  }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: 'var(--text)' }}>
                      {alert.message || alert.alert_type?.replace(/_/g, ' ')}
                    </p>
                    {alert.worker_name && <p style={{ color: 'var(--text-muted)' }}>{alert.worker_name}</p>}
                  </div>
                  <span className="flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{minutesAgo(alert.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
